import { ReplyCategory } from "core/types/takedown.types";

export type ChipColor = "default" | "success" | "warning" | "error" | "info";

/**
 * Unica fonte di verita' per etichetta e colore delle categorie di risposta.
 *
 * Prima esisteva una mappa parziale dentro RepliesInboxPage e un `chip label={r.category}` grezzo
 * dentro takedown.tsx: le categorie non mappate finivano a schermo come slug in grigio. Erano
 * proprio le piu' gravi — `counter_notice` (219 occorrenze sulla pratica 77), `rejected`,
 * `legal_threat` — che quindi NON si distinguevano da una risposta automatica innocua.
 *
 * Allineata a libs/takedown/inbox_classify.py (_RULES) piu' le categorie assegnate a valle da
 * inbox.py (`auto_rerouted`) e il fallback `other`.
 */
export const CATEGORY_META: Record<ReplyCategory, { labelKey: string; color: ChipColor }> = {
  // --- critiche: qualcuno contesta o minaccia, va guardato subito ---
  legal_threat: { labelKey: "takedown.replies.category.legal_threat", color: "error" },
  counter_notice: { labelKey: "takedown.replies.category.counter_notice", color: "error" },
  rejected: { labelKey: "takedown.replies.category.rejected", color: "error" },
  direct_from_site: { labelKey: "takedown.replies.category.direct_from_site", color: "error" },
  extortion_suspect: { labelKey: "takedown.replies.category.extortion_suspect", color: "error" },
  // --- richiedono un'azione nostra ---
  request_form: { labelKey: "takedown.replies.category.request_form", color: "warning" },
  request_info_mandate: { labelKey: "takedown.replies.category.request_info_mandate", color: "warning" },
  other: { labelKey: "takedown.replies.category.other", color: "warning" },
  // --- informative: il processo sta andando avanti da solo ---
  removed_confirmed: { labelKey: "takedown.replies.category.removed_confirmed", color: "success" },
  action_taken: { labelKey: "takedown.replies.category.action_taken", color: "info" },
  ack_ticket: { labelKey: "takedown.replies.category.ack_ticket", color: "info" },
  auto_rerouted: { labelKey: "takedown.replies.category.auto_rerouted", color: "info" },
  non_host_redirect: { labelKey: "takedown.replies.category.non_host_redirect", color: "default" },
  auto_reply: { labelKey: "takedown.replies.category.auto_reply", color: "default" },
  unrelated: { labelKey: "takedown.replies.category.unrelated", color: "default" },
};

/** Etichetta+colore con fallback esplicito: una categoria nuova lato backend non deve mai
 *  apparire muta. Meglio lo slug marcato "da mappare" che un chip grigio indistinguibile. */
export const categoryMeta = (
  category: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): { label: string; color: ChipColor } => {
  const m = CATEGORY_META[category as ReplyCategory];
  if (m) return { label: t(m.labelKey), color: m.color };
  return { label: category || "—", color: "warning" };
};
